import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead, { faqSchema } from '@components/marketing/SEOHead';
import PageHead from '@components/marketing/PageHead';
import CtaBand from '@components/marketing/CtaBand';
import { COURTS } from './content';

const SCREENS = [
  ['Court screen', 'One match, full screen, refreshing itself. Set it up once on a television at the venue and leave it for the rest of the day — it keeps showing whatever is on that court.'],
  ['Order of play', 'Every court at a venue on one screen: what is on now, and what follows on each.'],
  ['City desk', 'A whole day across every sport in one place, with what is live, what has finished and what is still to come.'],
  ['Results', 'Every decided match, searchable, with its full scorecard behind it.'],
];

const FAQS = [
  ['How does the score get from the court to the screen?',
   'The scorer enters it on a phone at the court. The score is published as they go, so the boards and the public site show it while the match is still being played rather than after it finishes.'],
  ['What do we need at the venue?',
   'A television or monitor with a browser, and a connection. The court screen is a web page — open it once on the right address and it keeps itself current without anyone touching it again.'],
  ['Can two people score the same match by mistake?',
   'No. A scorer account is signed in on one device at a time. Signing in somewhere else releases the first device, and the coordinator can see which of their scorers is currently on one.'],
  ['Can spectators follow it without an account?',
   'Yes. Results, standings and record boards are on the association’s public site and need no login.'],
];

const LiveScoringPage = () => (
  <>
    <SEOHead
      title="Live scoring and results boards"
      description="Score on a phone at the court and it reaches the venue screens while the match is still being played. Court screens, order of play, city desk and searchable results."
      schema={faqSchema(FAQS)}
    />

    <PageHead title="On the boards before the match is over" image="/images/sportstms/board_screen.jpg">
          <p>
            A scorer works from a phone a few feet from the play. What they enter is on the
            boards at the venue, and on the association’s public site, before the match is over.
          </p>
    </PageHead>

    <section className="stms-sec stms-sec--ink">
      <div className="stms-wrap stms-split">
        <div className="stms-split__say">
          <h2>A screen is always somewhere specific</h2>
          <p>
            That is why the boards are addressed the way a venue is: city, then sport, then
            court. A display set up once comes back to the same court on its own, which
            matters when nobody at the venue wants to be responsible for it.
          </p>
          <Link className="stms-btn stms-btn--ghost" to="/demo">See it running</Link>
        </div>
        <div className="stms-split__do">
          <div className="stms-board stms-board--flat" aria-label="Order of play">
            <div className="stms-board__top">
              <span className="stms-board__where">Order of play</span>
              <span className="stms-board__when">Four courts, one venue</span>
            </div>
            <ul className="stms-board__list">
              {COURTS.map((c) => (
                <li key={c.unit} className={c.live ? 'is-live' : ''}>
                  <span className="stms-board__unit">
                    {c.live && <i className="stms-live" aria-hidden="true" />}
                    {c.unit}
                  </span>
                  <span className="stms-board__tie">
                    <b>{c.home}</b><em>{c.score}</em><b>{c.away}</b>
                  </span>
                  <span className="stms-board__meta">{c.sport} · {c.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="stms-sec">
      <div className="stms-wrap">
        <div className="stms-lede">
          <h2>Four ways to show it</h2>
          <p>The same results, addressed for whoever is looking — a television at the court, or an organiser at a desk.</p>
        </div>
        <dl className="stms-caps">
          {SCREENS.map(([term, def]) => (
            <div key={term}><dt>{term}</dt><dd>{def}</dd></div>
          ))}
        </dl>
      </div>
    </section>

    <section className="stms-sec stms-sec--paper">
      <div className="stms-wrap stms-split stms-split--flip">
        <div className="stms-split__say">
          <h2>The phone is the scoresheet</h2>
          <p>
            A scorer sees only the sport and city they were given, and only the matches on
            their list. They check the players in, score the match point by point with an undo
            to hand, and save the result when it is done.
          </p>
          <Link className="stms-btn stms-btn--line" to="/sports">How each sport is scored</Link>
        </div>
        <div className="stms-split__do">
          <figure className="stms-figure">
            <img src="/images/sportstms/scorer.jpg" alt="An official scoring a badminton match on a phone at courtside" loading="lazy" width="1400" height="1050" />
          </figure>
        </div>
      </div>
    </section>

    <section className="stms-sec">
      <div className="stms-wrap">
        <div className="stms-lede"><h2>Common questions</h2></div>
        <dl className="stms-faq">
          {FAQS.map(([q, a]) => (
            <div key={q}><dt>{q}</dt><dd>{a}</dd></div>
          ))}
        </dl>
      </div>
    </section>

    <CtaBand title="Put a board on your venue screen" image="/images/sportstms/arena.jpg" action="Book a demo">
      <p>We will set one up on your own fixtures so you can see what the hall would see.</p>
    </CtaBand>
  </>
);

export default LiveScoringPage;
